/**
 * Sticky mobile buy bar (product page).
 *
 * Variant fidelity: this element deliberately has NO form and NO variant
 * state of its own. Its button submits Dawn's real product form, so
 * ProductForm.onSubmitHandler builds FormData from that form at submit
 * time and always reads the currently selected variant. There is nothing
 * here to fall out of sync.
 *
 * The bar must stay OUTSIDE <product-form>: ProductForm grabs
 * querySelector('[type="submit"]') for its loading state, so a submit
 * button inside it would hijack Dawn's own button.
 *
 * Visibility: shown only when none of the watched elements are on screen —
 * the main add to cart (so it appears once that scrolls away), the
 * ship-window banner and the footer (so it never covers either).
 *
 * Depends on constants.js and pubsub.js, both loaded earlier in
 * layout/theme.liquid, so PUB_SUB_EVENTS and subscribe() exist by the time
 * this deferred script runs.
 */

if (!customElements.get('sticky-buy-bar')) {
  customElements.define(
    'sticky-buy-bar',
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-sticky-buy-bar-add]');
        this.priceEl = this.querySelector('[data-sticky-buy-bar-price]');
        this.spinner = this.querySelector('.loading__spinner');
        this.form = document.querySelector('product-form form[data-type="add-to-cart-form"]');

        // Without the real form there is nothing safe to submit, so stay hidden.
        if (!this.form || !this.button) return;

        this.button.addEventListener('click', this.onClick.bind(this));

        this.watchScroll();
        this.watchVariant();
        this.watchCart();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScrollBound);
        window.removeEventListener('resize', this.onScrollBound);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.variantUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
      }

      /* ---------------------------------------------------------------- */

      watchScroll() {
        this.addToCart = document.querySelector('[id^="ProductSubmitButton-"]');

        // Things the bar must never sit on top of.
        //
        // The footer is deliberately NOT in this list. It was, until measuring
        // real pages showed these PDPs scroll only 1.1-1.4 viewports on a
        // phone while the footer is ~330px of that, so the footer reached the
        // bar's zone BEFORE the add to cart left the screen — by 8px on one
        // product and 119px on another. The bar could therefore never appear
        // anywhere. Decided 2026-08-17 to let the bar overlap the top of the
        // footer at maximum scroll rather than have it never show. Re-add
        // '.footer' here if product pages ever get long enough to afford it.
        this.blockers = [document.querySelector('.ship-window')].filter(Boolean);

        if (!this.addToCart) return;

        this.onScrollBound = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScrollBound, { passive: true });
        window.addEventListener('resize', this.onScrollBound, { passive: true });
        this.update();
      }

      onScroll() {
        // Coalesce to one measurement per frame.
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        // Hidden by CSS (desktop) — nothing to compute.
        const barHeight = this.offsetHeight;
        if (!barHeight) return;

        // "Scrolled past", not merely "off screen". Off screen is also true
        // before you have reached the button, which would show the bar at the
        // top of the page.
        const passedAddToCart = this.addToCart.getBoundingClientRect().bottom <= 0;

        // Only hide for something that would actually sit under the bar. The
        // earlier version hid whenever a blocker was anywhere in the viewport,
        // which left the bar usable for only a few hundred pixels of scroll.
        const zoneTop = window.innerHeight - barHeight;
        const blocked = this.blockers.some((el) => {
          const rect = el.getBoundingClientRect();
          return rect.bottom > zoneTop && rect.top < window.innerHeight;
        });

        this.toggle(passedAddToCart && !blocked);
      }

      toggle(show) {
        this.classList.toggle('is-visible', show);
        // Keep the duplicate add to cart out of the tab order and the
        // accessibility tree while it is off screen.
        if (show) this.removeAttribute('inert');
        else this.setAttribute('inert', '');
      }

      /* ---------------------------------------------------------------- */

      watchVariant() {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { sectionId, html, variant } = event.data;

          // Reuse the section Dawn already re-rendered rather than
          // reformatting money here — this keeps sale, unit and currency
          // formatting identical to the main price block.
          const newPrice = html.getElementById(`price-${sectionId}`);
          if (newPrice && this.priceEl) this.priceEl.innerHTML = newPrice.innerHTML;

          // Mirror Dawn's own button state instead of re-deriving it.
          const newButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
          if (newButton) {
            this.button.disabled = newButton.hasAttribute('disabled');
            const label = this.button.querySelector('span');
            const newLabel = newButton.querySelector('span');
            if (label && newLabel) label.textContent = newLabel.textContent.trim();
          } else if (variant) {
            this.button.disabled = variant.available === false;
          }
        });
      }

      watchCart() {
        const stop = () => this.setLoading(false);
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, stop);
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, stop);
      }

      /* ---------------------------------------------------------------- */

      onClick() {
        if (this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);

        // Submitting the real form is what guarantees the correct variant.
        // requestSubmit fires the submit event, which is what Dawn listens for.
        this.form.requestSubmit();
      }

      setLoading(isLoading) {
        this.button.classList.toggle('loading', isLoading);
        this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.spinner?.classList.toggle('hidden', !isLoading);
      }
    }
  );
}
